import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '@/lib/cn';
import { statusDisplay } from '@/lib/types';
const statusColors = {
    pending_approval: 'bg-purple-50 text-purple-700 ring-purple-600/20 dark:bg-purple-950/40 dark:text-purple-300',
    new: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-950/40 dark:text-sky-300',
    open: 'bg-cyan-50 text-cyan-700 ring-cyan-600/20 dark:bg-cyan-950/40 dark:text-cyan-300',
    in_progress: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-950/40 dark:text-amber-300',
    on_hold: 'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-950/40 dark:text-violet-300',
    resolved: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-950/40 dark:text-emerald-300',
    closed: 'bg-slate-100 text-slate-600 ring-slate-500/20 dark:bg-slate-800 dark:text-slate-400',
    cancelled: 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-950/40 dark:text-rose-300',
    active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-950/40 dark:text-emerald-300',
    prospect: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-950/40 dark:text-sky-300',
    terminated: 'bg-rose-50 text-rose-700 ring-rose-600/20',
    inactive: 'bg-slate-100 text-slate-600 ring-slate-500/20',
};
const priorityColors = {
    P1: 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-950/40 dark:text-rose-300',
    P2: 'bg-orange-50 text-orange-700 ring-orange-600/20 dark:bg-orange-950/40 dark:text-orange-300',
    P3: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-950/40 dark:text-amber-300',
    P4: 'bg-slate-100 text-slate-600 ring-slate-500/20 dark:bg-slate-800 dark:text-slate-400',
};
const priorityDots = {
    P1: 'bg-rose-500',
    P2: 'bg-orange-500',
    P3: 'bg-amber-500',
    P4: 'bg-slate-400',
};
export function Badge({ children, color, className, }) {
    return (_jsx("span", { className: cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset', color ?? 'bg-slate-100 text-slate-700 ring-slate-500/20 dark:bg-slate-800 dark:text-slate-300', className), children: children }));
}
// Reason-qualified hold display (2026-08-18, owner-approved — ITIL /
// ServiceNow pattern): one On Hold state for the SLA engine, but the HOLD
// REASON becomes the visible label so "SLA parked for a far-out visit" can
// never be confused with "waiting on the customer/vendor". Tickets without
// a recognised reason (or any other status) render exactly as before.
const HOLD_REASON_DISPLAY = {
    scheduled: {
        label: 'Visit scheduled (later)',
        color: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20 dark:bg-indigo-950/40 dark:text-indigo-300',
    },
    waiting_customer: { label: 'On hold — awaiting customer', color: statusColors.on_hold },
    waiting_vendor: { label: 'On hold — awaiting vendor', color: statusColors.on_hold },
    waiting_parts: { label: 'On hold — awaiting parts', color: statusColors.on_hold },
};
export function StatusBadge({ status, substatus }) {
    if (status === 'on_hold' && substatus) {
        const d = HOLD_REASON_DISPLAY[substatus];
        if (d)
            return _jsx(Badge, { color: d.color, children: d.label });
    }
    return _jsx(Badge, { color: statusColors[status], children: statusDisplay(status) });
}
export function PriorityBadge({ priority }) {
    return (_jsxs("span", { className: cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset', priorityColors[priority]), children: [_jsx("span", { className: cn('h-1.5 w-1.5 rounded-full', priorityDots[priority]) }), priority] }));
}
